// import { GiftItem, Vendor } from '../types';
import { API_CONFIG, API_ENDPOINTS } from './config';

// ₦36,600 or ₦8,095.20 — kobo only when there are kobo.
export const formatNaira = (amount: number): string => {
  const value = Math.round((Number(amount) || 0) * 100) / 100;
  const hasKobo = Math.round(value * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: hasKobo ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
};

export const confirmPayment = async (reference: string): Promise<{ success: boolean; message: string; data?: any }> => {
  try {
    const url = `${API_ENDPOINTS.PAYMENT_CONFIRM}?reference=${encodeURIComponent(reference)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return { success: false, message: `Error confirming payment: ${errorMessage}` };
  }
};

export const confirmSubscriptionPayment = async (
  reference: string
): Promise<{ success: boolean; message: string; data?: any }> => {
  try {
    const url = `${API_ENDPOINTS.SUBSCRIPTION_PAYMENT_CONFIRM}?reference=${encodeURIComponent(reference)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return { success: false, message: `Error confirming subscription payment: ${errorMessage}` };
  }
};

interface SubscriptionGift {
  _id: string;
  name: string;
  description?: string;
  image_url?: string;
  price?: number;
  status?: string;
}

export const fetchSubscriptionGifts = async (): Promise<{
  success: boolean;
  message: string;
  data?: SubscriptionGift[];
}> => {
  try {
    const response = await fetch(API_ENDPOINTS.SUBSCRIPTION_GIFTS, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: data.success === true,
      message: data.message || '',
      data: Array.isArray(data.data) ? data.data : [],
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return { success: false, message: `Error fetching gifts: ${errorMessage}` };
  }
};

export const chooseSubscriptionGift = async (payload: {
  giftId: string;
  payment_reference: string;
}): Promise<{ success: boolean; message: string; data?: any }> => {
  try {
    const response = await fetch(API_ENDPOINTS.SUBSCRIPTION_GIFT_CHOOSE, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const rawText = await response.text();
    let parsedResponse: any = null;

    try {
      parsedResponse = rawText ? JSON.parse(rawText) : null;
    } catch {
      parsedResponse = null;
    }

    if (!response.ok) {
      return {
        success: false,
        message:
          parsedResponse?.message ||
          `Gift selection failed with status ${response.status}.`,
      };
    }

    return parsedResponse ?? { success: false, message: 'Empty response from gift service.' };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return { success: false, message: `Error choosing gift: ${errorMessage}` };
  }
};

interface SubscriptionCreatePayload {
  name: string;
  type: 'self' | 'gift';
  category: string;
  frequency: string;
  paymentMode?: 'daily' | 'monthly';
  subscribedBy: {
    name: string;
    email_address: string;
    phone_number: string;
  };
  subscribedFor: {
    name: string;
    email_address: string;
    phone_number: string;
    delivery_address: string;
    business_address?: string;
    selling_spot_name?: string;
  };
  vendor?: string;
  items: Array<{
    item: string;
    item_name: string;
    quantity: number;
    type?: string;
    color?: string;
    size?: string;
    brand?: string;
  }>;
  deliveryFrequency?: string;
  deliveryDays?: string[];
  deliveryTimeSlot?: string;
  dropoffDay?: string;
  message: string;
  totalAmount: number;
  callback_url: string;
}

const SUBSCRIPTION_PAYLOAD_PREVIEW_STORAGE_KEY = 'boqbox_subscription_payload_preview_v1';

export const getSubscriptionPayloadPreview = ():
  | (Omit<SubscriptionCreatePayload, 'callback_url'> & { callback_url: string })
  | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.sessionStorage.getItem(SUBSCRIPTION_PAYLOAD_PREVIEW_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const clearSubscriptionPayloadPreview = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(SUBSCRIPTION_PAYLOAD_PREVIEW_STORAGE_KEY);
};

export const createSubscription = async (
  payload: Omit<SubscriptionCreatePayload, 'callback_url'>
): Promise<{ success: boolean; message: string; payment_url?: string; data?: any }> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
  const requestBody = {
    ...payload,
    callback_url: API_CONFIG.SUBSCRIPTION_CALLBACK_URL,
  };

  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(
      SUBSCRIPTION_PAYLOAD_PREVIEW_STORAGE_KEY,
      JSON.stringify(requestBody)
    );
  }

  try {
    const response = await fetch(API_ENDPOINTS.SUBSCRIPTION_CREATE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
      body: JSON.stringify(requestBody),
    });

    const rawText = await response.text();
    let parsedResponse: any = null;

    try {
      parsedResponse = rawText ? JSON.parse(rawText) : null;
    } catch {
      parsedResponse = null;
    }

    if (!response.ok) {
      return {
        success: false,
        message:
          parsedResponse?.message ||
          `Subscription create failed with status ${response.status}.`,
      };
    }

    return parsedResponse ?? { success: false, message: 'Empty response from subscription service.' };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return {
        success: false,
        message: 'The subscription service took too long to respond. Please try again.',
      };
    }

    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return { success: false, message: `Error creating subscription: ${errorMessage}` };
  } finally {
    window.clearTimeout(timeoutId);
  }
};

